<template>
  <div>
    <base-card class="tw-mb-3">
      <div class="tw-flex tw-justify-between tw-mb-6">
        <div class="tw-flex tw-flex-col">
          <div class="tw-flex">
            <div class="tw-text-gray-800 tw-font-normal">{{ plan.planName }}</div>
            <base-badge class="tw-ml-2 tw-my-auto" :color="statusColor(plan.status)" size="sm">{{ capitalize(plan.status).replace(/_/g, " ") }}</base-badge>
            <base-badge v-if="plan.isMerge == true" class="tw-ml-2" size="sm" color="gray">Merged</base-badge>
          </div>
          <div class="tw-text-gray-500 tw-text-sm tw-mt-1">{{ plan.businessLocationName }}</div>
        </div>
        <base-dropdown class="tw-my-auto" v-if="isLocationFiltered === false" :options="actionOptions" :showBackground="false" align="right">
          <base-icon>
            <more-vertical-icon class="tw-w-6 tw-h-6" />
          </base-icon>
          <template #option="{ option: label }">
            <div v-if="label === 'separator'" class="tw-w-full tw-bg-gray-200" style="height:1px;"></div>
            <button v-else class="tw-flex tw-items-center tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600 focus:tw-outline-none" @click="actionLink(label)">
              {{ label }}
            </button>
          </template>
        </base-dropdown>
      </div>
      <div v-if="plan.status.toLowerCase() === 'failed payment'">
        <base-field title="Next Failed Payment Retry">
          <div class="tw-text-red-600 tw-mr-1">{{ formattedNextPaymentAttempt }}</div>
        </base-field>
      </div>
      <div class="tw-grid tw-grid-cols-2 tw-mt-3 tw-gap-4">
        <div class="tw-grid tw-grid-rows-2 tw-gap-4">
          <base-field title="Billing Cycle" :value="formattedCycleText" />
          <base-field title="Next Invoice" :value="formattedNextInvoiceDate" />
        </div>
        <div class="tw-grid tw-grid-rows-2 tw-gap-4">
          <base-field title="Plan Start" :value="localStartDate" />
          <base-field v-if="plan.isAutoRenew === true" title="Plan End">
            <div class="tw-flex">
              <div class="tw-text-gray-800 tw-mr-1">Auto-Renew</div>
              <arrow-renew-icon class="tw-w-3 tw-h-3 tw-my-auto tw-text-gray-800" />
            </div>
          </base-field>
          <base-field v-else title="Plan End">
            {{ cancelAfter }}
          </base-field>
        </div>
      </div>
      <!-- credits -->
      <div v-if="organization.useCreditsSetting === true" class="tw-border-t tw-mt-4 tw-flex tw-justify-between tw-items-center tw-pt-3">
        <div class="tw-flex">
          <div class="tw-font-normal tw-text-lg tw-text-primary-500">
            {{ plan.creditsBalance }}
          </div>
          <span class="tw-flex tw-ml-2 tw-my-auto tw-text-sm">credits available</span>
        </div>
        <div class="tw-flex tw-gap-5 sm:tw-gap-3">
          <div v-if="isActive">
            <base-button class="tw-hidden sm:tw-block" type="flat" size="sm" @click="showCreditRecharge"
              >Recharge<template v-slot:icon>
                <arrow-renew-icon class="tw-h-3.5 tw-w-3.5" />
              </template>
            </base-button>
            <base-icon class="tw-block sm:tw-hidden tw-my-auto" color="primary" @click="showCreditRecharge"> <arrow-renew-icon class="tw-h-3.5 tw-w-3.5"/></base-icon>
          </div>
          <div v-if="canAdjustCredits">
            <base-button class="tw-hidden sm:tw-block" type="flat" size="sm" @click="showCreditAdjust"
              >Adjust<template v-slot:icon>
                <plus-minus-icon />
              </template>
            </base-button>
            <base-icon class="tw-block sm:tw-hidden tw-my-auto" color="primary" size="sm" @click="showCreditAdjust"> <plus-minus-icon class="tw-w-4 tw-h-4"/></base-icon>
          </div>
          <base-button type="primary" size="sm" @click="showCreditRedeem">
            Redeem
            <template v-slot:icon>
              <shopping-bag-icon />
            </template>
          </base-button>
        </div>
      </div>

      <!-- Customer Card Section (inside same card) -->
      <div class="tw-border-t tw-mt-4 tw-pt-4" v-if="cobrandId === 'rx-harmony'" v-show="false">
        <customer-card
          ref="customerCard"
          :business-id="businessId"
          :user-id="userId"
          :user-business-plan-id="plan.userBusinessPlanId"
          :subscription-id="plan.stripeSubscriptionId"
          :currency="plan.currency"
          :default-expanded="true"
        />
      </div>
    </base-card>

    <business-member-plan-edit ref="editModal" :businessId="businessId" :userId="userId" :plan="plan" @refresh="refresh" />
    <business-member-plan-pause
      ref="pauseModal"
      :businessId="businessId"
      :userId="userId"
      :userBusinessPlanId="plan.userBusinessPlanId"
      :useCreditsSetting="organization.useCreditsSetting"
      @refresh="refresh"
    />
    <business-member-plan-resume ref="resumeModal" :businessId="businessId" :userId="userId" :userBusinessPlanId="plan.userBusinessPlanId" @refresh="refresh" />
    <business-member-plan-reactivate ref="reactivateModal" :businessId="businessId" :userId="userId" :userBusinessPlanId="plan.userBusinessPlanId" @refresh="refresh" />
    <business-member-plan-cancel ref="cancelModal" :businessId="businessId" :userId="userId" :plan="plan" :isAutoActions="false" @refresh="refresh" />
    <business-member-plan-agreement ref="agreementModal" :userId="userId" :plan="plan" />
    <business-member-plan-change-location
      ref="locationChangeModal"
      :businessId="businessId"
      :userBusinessPlanId="plan.userBusinessPlanId"
      :businessLocationId="plan.businessLocationId"
    />
    <business-member-plan-delete ref="deleteModal" :businessId="businessId" :userId="userId" :userBusinessPlanId="plan.userBusinessPlanId" :status="plan.status" />
    <credit-adjust ref="creditAdjust" :business-id="businessId" :user-id="userId" :plan="plan" @save="refresh" />
    <credit-recharge ref="creditRecharge" :business-id="businessId" :user-id="userId" :plan="plan" @refresh="refresh" />
    <credit-redeem ref="creditRedeem" :business-id="businessId" :user-id="userId" :plan="plan" :member-full-name="memberFullName" @refresh="refresh" />
    <stripe-ids-modal ref="stripeIdsModal" :businessId="businessId" :userId="userId" :userBusinessPlanId="plan.userBusinessPlanId" />
  </div>
</template>
<script>
import moment from "moment";
import CustomerCard from "@/views/business/member/plans/components/CustomerCard.vue";
import BusinessMemberPlanEdit from "@/views/business/member/plans/components/BusinessMemberPlanEdit.vue";
import BusinessMemberPlanChangeLocation from "@/views/business/member/plans/components/BusinessMemberPlanChangeLocation.vue";
import BusinessMemberPlanPause from "@/views/business/member/plans/components/BusinessMemberPlanPause.vue";
import BusinessMemberPlanCancel from "@/views/business/member/plans/components/BusinessMemberPlanCancel.vue";
import BusinessMemberPlanDelete from "@/views/business/member/plans/components/BusinessMemberPlanDelete.vue";
import BusinessMemberPlanReactivate from "@/views/business/member/plans/components/BusinessMemberPlanReactivate.vue";
import BusinessMemberPlanResume from "@/views/business/member/plans/components/BusinessMemberPlanResume.vue";
import BusinessMemberPlanAgreement from "@/views/business/member/plans/components/BusinessMemberPlanAgreement";
import StripeIdsModal from "@/views/business/member/plans/components/StripeIdsModal.vue";
import CreditAdjust from "@/views/business/member/plans/components/CreditAdjust.vue";
import CreditRecharge from "@/views/business/member/plans/components/CreditRecharge.vue";
import CreditRedeem from "@/views/business/member/plans/components/CreditRedeem.vue";
import { mapGetters } from "vuex";
import { capitalize } from "lodash";
import { MoreVerticalIcon, ArrowRenewIcon, ShoppingBagIcon, PlusMinusIcon } from "@/assets/svgs";

export default {
  name: "BusinessMemberPlan",
  components: {
    CustomerCard,
    BusinessMemberPlanEdit,
    BusinessMemberPlanPause,
    BusinessMemberPlanCancel,
    BusinessMemberPlanReactivate,
    BusinessMemberPlanResume,
    BusinessMemberPlanAgreement,
    BusinessMemberPlanDelete,
    BusinessMemberPlanChangeLocation,
    StripeIdsModal,
    CreditAdjust,
    CreditRecharge,
    CreditRedeem,
    MoreVerticalIcon,
    ArrowRenewIcon,
    ShoppingBagIcon,
    PlusMinusIcon,
  },
  props: {
    businessId: String,
    userId: String,
    plan: Object,
    refreshId: Number,
    memberFullName: String,
  },
  data() {
    return {
      capitalize,
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters("organization", ["organization", "cobrandId"]),
    ...mapGetters("role", ["isLocationFiltered", "isPredictile", "isBusinessAdministrator", "isBusinessManagement", "isBusinessOperations", "isPscSecondaryRole"]),
    actionOptions() {
      let options = [];
      options.push("View Agreement");
      if (this.canViewStripeIds) options.push("View Stripe IDs");
      options.push("separator");
      if (this.canActivatePlan === true) options.push("Activate Plan");
      if (this.isActive && !this.isPscSecondaryRole) options.push("Edit Plan");
      if (this.isLocationFiltered === false && this.plan.status.toLowerCase() !== "deleted" && !this.isPscSecondaryRole) options.push("Change Location");
      if (this.canPausePlan === true && !this.isPscSecondaryRole) options.push("Pause Plan");
      if (this.canCancelPlan === true) options.push("Cancel Plan");
      if (this.canCancelPlan === true) options.push("Cancel with Auto Deletion");
      if (this.canResumePlan === true) options.push(this.pauseRestartButton);
      if (this.canDeletePlan === true && this.plan.status.toLowerCase() !== "deleted") options.push("Delete Plan");

      return options;
    },
    locale() {
      return window.navigator.language;
    },
    formattedCycleText() {
      var cycle = this.plan.cycleFrequency > 1 ? "every " + this.plan.cycleFrequency + " " + this.plan.cycleInterval : "/ " + this.plan.cycleInterval;
      return this.formattedPrice + " " + cycle;
    },
    formattedPrice() {
      return new Intl.NumberFormat(this.locale, { style: "currency", currency: this.plan.currency }).format(this.plan.priceCents / 100);
    },
    paymentEnd() {
      if (this.plan.isAutoRenew) {
        return "---";
      } else {
        if (this.plan.cycleEndCount > 0) {
          return this.plan.cycleEndCount + " cycles";
        } else {
          const formattedCycleEndDate = moment
            .utc(this.plan.cycleEndDate)
            .local()
            .format("MMM DD, YYYY h:mm a");
          return formattedCycleEndDate;
        }
      }
    },
    cancelAfter() {
      if (this.plan.isAutoRenew) {
        return "---";
      } else {
        if (this.plan.cancelAfterCycleCount > 0) {
          return "Plan canceled after " + this.plan.cancelAfterCycleCount + " payment cycles";
        } else {
          const formattedCancelAfterDate = moment
            .utc(this.plan.cancelAfterDate)
            .local()
            .format("MMM DD, YYYY h:mm a");
          return "Plan canceled after " + formattedCancelAfterDate;
        }
      }
    },
    formattedNextInvoiceDate: {
      get() {
        return this.plan.currentPeriodEnd
          ? moment
              .utc(this.plan.currentPeriodEnd)
              .local()
              .format("MMM DD, YYYY h:mm a")
          : "---";
      },
    },
    localStartDate: {
      get() {
        return moment
          .utc(this.plan.startDate)
          .local()
          .format("MMM DD, YYYY");
      },
    },
    formattedNextPaymentAttempt: {
      get() {
        return this.plan.nextPaymentAttempt
          ? moment
              .utc(this.plan.nextPaymentAttempt)
              .local()
              .format("MMM DD, YYYY h:mm a")
          : "---";
      },
    },
    pauseRestartButton() {
      if (this.plan.status.toLowerCase() === `inactive` || this.plan.status.toLowerCase() === `cancelled`) {
        return "Restart Plan";
      } else {
        return "Resume Plan";
      }
    },
    isActive: {
      get() {
        if (
          this.plan.status &&
          (this.plan.status.toLowerCase() == "active" ||
            this.plan.status.toLowerCase() == "active - payments complete" ||
            this.plan.status.toLowerCase() === "activated - pending start")
        )
          return true;
        else return false;
      },
    },
    canPausePlan() {
      return (
        (this.isBusinessManagement(this.organization.id) || this.isPredictile(this.organization.id)) &&
        ["active", "activated - pending start"].indexOf(this.plan.status.toLowerCase()) >= 0
      );
    },
    canActivatePlan() {
      return (this.isBusinessOperations(this.organization.id) || this.isPredictile(this.organization.id)) && ["awaiting payment"].indexOf(this.plan.status.toLowerCase()) >= 0;
    },
    canCancelPlan() {
      return (
        (this.isBusinessManagement(this.organization.id) || this.isPredictile(this.organization.id)) &&
        ["active", "active - payments complete", "activated - pending start", "awaiting payment", "paused", "failed payment", "awaiting payment"].indexOf(
          this.plan.status.toLowerCase(),
        ) >= 0
      );
    },
    canResumePlan() {
      return (this.isBusinessOperations(this.organization.id) || this.isPredictile(this.organization.id)) && ["paused", "cancelled"].indexOf(this.plan.status.toLowerCase()) >= 0;
    },
    canDeletePlan() {
      return (this.isBusinessManagement(this.organization.id) || this.isPredictile(this.organization.id)) && ["cancelled"].indexOf(this.plan.status.toLowerCase()) >= 0;
    },
    canAdjustCredits() {
      return this.isBusinessManagement(this.organization.id) || this.isPredictile(this.organization.id);
    },
    canViewStripeIds() {
      return this.isLocationFiltered === false && (this.isBusinessAdministrator(this.organization.id) || this.isPredictile(this.organization.id));
    },
  },
  methods: {
    actionLink(label) {
      switch (label) {
        case "Edit Plan":
          this.openEditModal();
          break;
        case "Pause Plan":
          this.openPauseModal();
          break;
        case "Cancel Plan":
          this.openCancelModal(false);
          break;
        case "Cancel with Auto Deletion":
          this.openCancelModal(true);
          break;
        case "Delete Plan":
          this.openDeleteModal();
          break;
        case "Resume Plan":
          this.openResumeModal();
          break;
        case "Restart Plan":
          this.openReactivateModal();
          break;
        case "Activate Plan":
          this.activate();
          break;
        case "View Agreement":
          this.openAgreementModal();
          break;
        case "Change Location":
          this.openChangeLocationModal();
          break;
        case "View Stripe IDs":
          this.showStripeIds();
          break;
      }
    },
    pauseRestartButtonAction() {
      if (this.plan.status.toLowerCase() === `inactive` || this.pla.status.toLowerCase() === `cancelled` || (this.plan.status.toLowerCase() === `paused` && this.isCancelled)) {
        this.openReactivateModal();
      } else if (this.status.toLowerCase() === `paused`) {
        this.openResumeModal();
      } else {
        this.openPauseModal();
      }
    },
    activate() {
      this.$emit("activate");
    },
    openAgreementModal() {
      this.$refs.agreementModal.show();
    },
    openReactivateModal() {
      this.$refs.reactivateModal.show();
    },
    openResumeModal() {
      this.$refs.resumeModal.show();
    },
    openEditModal() {
      this.$refs.editModal.show();
    },
    openPauseModal() {
      this.$refs.pauseModal.show();
    },
    openCancelModal(isAutoActions = false) {
      this.$refs.cancelModal.show();
      this.$refs.cancelModal.isAutoActions = isAutoActions;
    },
    openDeleteModal() {
      this.$refs.deleteModal.show();
    },
    openChangeLocationModal() {
      this.$refs.locationChangeModal.show();
    },
    showCreditAdjust() {
      this.$refs.creditAdjust.show();
    },
    showCreditRecharge() {
      this.$refs.creditRecharge.show();
    },
    showCreditRedeem() {
      this.$refs.creditRedeem.show();
    },
    showStripeIds() {
      this.$refs.stripeIdsModal.show();
    },
    refresh() {
      this.$emit("refresh");
      // Also refresh the customer card
      if (this.$refs.customerCard) {
        this.$refs.customerCard.refresh();
      }
    },
    statusColor(status) {
      switch (status.toLowerCase()) {
        case "active":
        case "activated - pending start":
        case "active - payments complete":
          return "green";
        case "paused":
        case "cancelled":
        case "awaiting payment":
          return "gray";
        case "failed payment":
          return "red";
        default:
          return "gray";
      }
    },
  },
};
</script>
